import { DetailSectionTemplateWithTranslation } from "components/MainSectionTemplate/translation/DetailSectionTemplateWithTranslation";
import { RenderEditOptions, Section } from "components/MainSectionTemplate/types";
import { PreferredTemplatePill } from "components/Pill/PreferredTemplatePill";
import { BasicTemplatePill } from "components/Pill/BasicTemplatePill";
import RequiredTextAreaInput from "components/ValidatedFields/RequiredTextAreaInput";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import { YesNoRadioGroupForInPageEditing, YesNoText } from "components/ValidatedFields/YesNoRadioGroup";
import React from "react";
import { replaceNewLineWithBr } from "utilities/reactUtils";
import { useIsSystemOneAccount } from "common/SessionContext";

interface Props {
  title: string
  invoiceReminderTemplate: server.dto.MessageTemplate
}

interface InvoiceReminderTemplateGeneralState {
  title: string
  preferredTemplate: boolean
  basicTemplate: boolean
}

export const InvoiceReminderTemplateDetailSection = (props: Props) => {
  const isSystemOneAccount = useIsSystemOneAccount();
  const [generalDetailsState, setGeneralDetailsState] = React.useState<InvoiceReminderTemplateGeneralState>({
    title: props.invoiceReminderTemplate.title,
    preferredTemplate: props.invoiceReminderTemplate.preferredTemplate,
    basicTemplate: props.invoiceReminderTemplate.basicTemplate,
  });

  const topSection: Section = {
    title: props.title,
    type: "regular",
    key: "generalSection",
    fields: [{
      key: "title",
      label: RESX.GeneralLabels.Name,
      render: () => <div>{generalDetailsState.title}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
          validatorKey="title"
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTitleLength}
          value={value}
          onChange={(name) => { onChange(name) }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: generalDetailsState.title,
        submitChange: (title: string) => window.CoreApi.MessageTemplate.UpdateDetails({
          expectAttachment: false,
          basicTemplate: generalDetailsState.basicTemplate,
          messageTemplateId: props.invoiceReminderTemplate.messageTemplateId,
          title,
          preferredTemplate: generalDetailsState.preferredTemplate,
        }),
      },
    }, {
      key: "basicTemplate",
      label: RESX.Advancing.ReadOnly,
      hidden: !isSystemOneAccount && !generalDetailsState.basicTemplate,
      render: () => <YesNoText value={generalDetailsState.basicTemplate} yesLabel={<BasicTemplatePill />} />,
      editDisabled: !isSystemOneAccount,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
          value={value}
          onChange={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: generalDetailsState.basicTemplate,
        submitChange: (basicTemplate: boolean) => window.CoreApi.MessageTemplate.UpdateDetails({
          expectAttachment: false,
          basicTemplate,
          messageTemplateId: props.invoiceReminderTemplate.messageTemplateId,
          title: generalDetailsState.title,
          preferredTemplate: generalDetailsState.preferredTemplate,
        }),
      }
    }, {
      key: "preferredTemplate",
      label: RESX.GeneralLabels.resxDefault,
      editDisabled: generalDetailsState.preferredTemplate,
      hidden: generalDetailsState.basicTemplate,
      render: () => <div>{generalDetailsState.preferredTemplate
        ? <PreferredTemplatePill />
        : <div>{RESX.GeneralLabels.No}</div>
      }</div>,
      inPageEdit: {
        render: ({ value, onChange }: RenderEditOptions<boolean>) => <YesNoRadioGroupForInPageEditing
          value={value}
          onChange={(v) => onChange(v, true)}
        />,
        fullWidth: true,
        type: "custom",
        initialValue: generalDetailsState.preferredTemplate,
        submitChange: (preferredTemplate: boolean) => window.CoreApi.MessageTemplate.UpdateDetails({
          expectAttachment: false,
          basicTemplate: generalDetailsState.basicTemplate,
          messageTemplateId: props.invoiceReminderTemplate.messageTemplateId,
          title: generalDetailsState.title,
          preferredTemplate,
        }),
      }
    }],
  }

  const onValueChanged = (key: string, value: any) => {
    setGeneralDetailsState(prev => ({
      ...prev,
      [key]: value
    }));
  }

  const isInPageEditable = (props.invoiceReminderTemplate.permissions.canModify && !generalDetailsState.basicTemplate) || isSystemOneAccount;

  return <DetailSectionTemplateWithTranslation
    queryKey={`invoiceReminderTemplate-${props.invoiceReminderTemplate.messageTemplateId}`}
    isInPageEditable={isInPageEditable}
    topSection={topSection}
    listApi={() => window.CoreApi.MessageTemplate.ListReminderTranslation(props.invoiceReminderTemplate.messageTemplateId)}
    addTranslationApi={(language) => window.CoreApi.MessageTemplate.CreateReminderTranslation({
      messageTemplateId: props.invoiceReminderTemplate.messageTemplateId,
      language,
    })}
    removeTranslationApi={(language) => window.CoreApi.MessageTemplate.DeleteTranslation({
      messageTemplateId: props.invoiceReminderTemplate.messageTemplateId,
      language,
    })}
    renderFieldsForTranslation={(language, formStateForTranslation) => [{
      key: "subject",
      label: RESX.Message.Subject,
      render: () => <div>{formStateForTranslation.subject}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextInput
          validatorKey="subject"
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTranslationSubjectLength}
          value={value}
          onChange={(title) => { onChange(title); }}
          onEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: formStateForTranslation.subject,
        submitChange: (subject: string) => window.CoreApi.MessageTemplate.SetReminderTranslation({
          messageTemplateId: props.invoiceReminderTemplate.messageTemplateId,
          subject,
          body: formStateForTranslation.body,
          language,
        }),
      },
    }, {
      key: "body",
      label: RESX.Message.Body,
      render: () => <div>{replaceNewLineWithBr(formStateForTranslation.body)}</div>,
      inPageEdit: {
        render: ({ value, onChange, submit, cancel }: RenderEditOptions<string>) => <RequiredTextAreaInput
          validatorKey="body"
          focusOnMount
          maxLength={serverReference.constants.messageTemplate.maxTranslationBodyLength}
          value={value}
          onChange={(title) => { onChange(title); }}
          onCtrlEnter={() => submit()}
          onEsc={() => cancel()}
        />,
        fullWidth: true,
        type: "confirmOrCancelButton",
        initialValue: formStateForTranslation.body,
        submitChange: (body: string) => window.CoreApi.MessageTemplate.SetReminderTranslation({
          messageTemplateId: props.invoiceReminderTemplate.messageTemplateId,
          subject: formStateForTranslation.subject,
          body,
          language,
        }),
      }
    }]}
    onValueChanged={onValueChanged}
  />
}
